import { ArmyState, getArmyBookData, resetLoadedBooks } from "@/data/redux/armySlice";
import { ListState, loadSavedList } from "@/data/redux/listSlice";
import { getGameRules } from "@/data/redux/rulesSlice";
import { RootState } from "@/data/redux/store";
import { loadSavedUnits } from "@/data/redux/unitsSlice";
import {
  GainType,
  GameSystems,
  IArmyBook,
  IArmyData,
  IFleetData,
  IFleetShipSelection,
  IFtlSavedListState,
  ISavedListState,
  ISelectedUnit,
  ISpecialRule,
  IUnit,
  IUpgradeGains,
  IUpgradeGainsWeapon,
  IViewPreferences
} from "@/data/types";
import { ISaveData } from "@/data/types/saveData";

import { STORAGE_KEYS } from "@/data/globals";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { groupBy, sumBy, uniq } from "lodash-es";
import { nanoid } from "nanoid";
import { calculateListTotal, calculateUnitTotal, getUnitRules, makeCopy } from "../Helpers";
import RulesService from "../rules/RulesService";
import UnitService from "../UnitService";
import UpgradeService from "../UpgradeService";
import FtlService from "./FtlService";

export default class PersistenceService {
  private static prefix = "AF_Save_";
  public static currentSaveVersion = 3;

  public static getSaveKey(id: string) {
    return this.prefix + id;
  }

  // #region Saving

  public static saveImport(saveName: any, json: string) {
    localStorage[this.prefix + saveName] = json;
  }

  public static saveIfNotExists(saveData: ISaveData) {
    if (!localStorage.hasOwnProperty(this.getSaveKey(saveData.id))) {
      localStorage[this.getSaveKey(saveData.id)] = JSON.stringify(saveData);
    }
  }

  public static createSave(
    listState: ListState,
    loadedBooks: IArmyData[] | IFleetData[],
    units?: ISelectedUnit[] | IFleetShipSelection[],
    communityList?: boolean
  ): string {
    const gameSystemId = GameSystems[listState.gameSystem]; // GameSystems[gameSystemId];
    const ftl = gameSystemId === GameSystems.ftl;
    const list: ListState = {
      ...listState,
      id: communityList ? nanoid(12) : listState.id,
      isCloud: false,
      modified: new Date().toJSON()
    };

    if (communityList) list.communityList = true;

    const armyData = loadedBooks[0];
    const saveData: ISaveData = {
      id: list.id,
      gameSystem: list.gameSystem, // Save as string version
      armyId: armyData.uid,
      armyIds: loadedBooks.map((x) => x.uid),
      armyFaction: (armyData as IArmyData).factionName,
      armyName: armyData.name,
      modified: new Date().toJSON(),
      saveVersion: this.currentSaveVersion,
      favourite: false,

      armyVersions: loadedBooks.map((x) => ({
        version: x.versionString || "1.0.0",
        armyId: x.uid
      })),

      // Conditional parts
      listPoints: ftl
        ? FtlService.calculateListTotal((units as IFleetShipSelection[]) || [])
        : calculateListTotal((units as ISelectedUnit[]) ?? []),
      list: ftl
        ? this.getDataForFtlSave(list, (units as IFleetShipSelection[]) || [])
        : this.getDataForSave(list, (units as ISelectedUnit[]) ?? [])
    };

    console.log("Creating save...", saveData);

    const saveDataJson = JSON.stringify(saveData);
    const saveKey = this.getSaveKey(saveData.id);
    localStorage[saveKey] = saveDataJson;

    return list.id;
  }

  public static updateSave(list: ListState, units: ISelectedUnit[], armyIds?: string[]) {
    return this.updateSaveData(list.id, (existingSave) => {
      const saveData: ISaveData = {
        ...existingSave,
        armyIds: uniq(units.map((x) => x.armyId).concat(armyIds ?? [])),
        modified: new Date().toJSON(),
        list: this.getDataForSave(list, units),
        listPoints: calculateListTotal(units),
        saveVersion: this.currentSaveVersion
      };

      console.log("Updating save...", saveData);

      return saveData;
    });
  }

  public static deleteSave(listId) {
    const key = Object.keys(localStorage).find((key) => key.endsWith(listId));
    if (key) delete localStorage[key];
  }

  public static duplicateSave(copy: ISaveData) {
    localStorage[this.getSaveKey(copy.id)] = JSON.stringify(copy);
  }

  // #endregion

  // #region Loading

  public static async load(
    dispatch: ThunkDispatch<RootState, any, any>,
    save: ISaveData,
    callback: (save: ISaveData) => void,
    reset: boolean = true
  ) {
    console.log("Loading save...", save);

    const gameSystem = GameSystems[save.gameSystem];

    const armyIds = save.armyIds || [save.armyId];

    dispatch(
      getArmyBookData({
        armyUids: armyIds,
        gameSystem,
        reset,
        options: {
          simpleMode: save.list.simpleMode || false,
          includeVehicles: save.list.includeVehicles || false,
          swarmMode: save.list.swarmMode || false
        }
      })
    ).then((res) => {
      const armyBooks = (res.payload as any).results as IArmyBook[];

      let versionChanged = (() => {
        if (!save.armyVersions) {
          return true;
        }
        for (const av of save.armyVersions) {
          const book = armyBooks.find((x) => x.uid === av.armyId);
          if (!book) continue; // ??
          if (book.versionString !== av.version) {
            return true;
          }
        }
        return false;
      })();

      const { list, loadedUnits } = this.buildListFromSave(save.list as ISavedListState, armyBooks);

      if (versionChanged) {
        // alert(
        //   "One or more of the army books used to build this list have been updated. Some units or upgrades may be missing from your list."
        // );
        list.versionChanged = true;
        console.log("Set version changed to true");
        this.updateSaveData(list.id, (save) => {
          save.armyVersions = armyBooks.map((x) => ({
            armyId: x.uid,
            version: x.versionString
          }));
          return save;
        });
      }

      dispatch(loadSavedList(list));
      dispatch(loadSavedUnits(loadedUnits));
      dispatch(getGameRules({ gameSystem, reset: true }));
      callback(save);
    });
  }

  public static async loadFromShare(
    dispatch: ThunkDispatch<RootState, any, any>,
    shareData: ISavedListState,
    callback: (armyBooks: IArmyBook[]) => void
  ) {
    console.log("Loading save...", shareData);

    const gameSystem = GameSystems[shareData.gameSystem];

    dispatch(resetLoadedBooks());
    const armyIds = uniq(shareData.units.map((x) => x.armyId));

    dispatch(
      getArmyBookData({
        armyUids: armyIds,
        gameSystem,
        reset: true,
        options: {
          simpleMode: shareData.simpleMode || false,
          includeVehicles: shareData.includeVehicles || false,
          swarmMode: shareData.swarmMode || false
        }
      })
    ).then((res) => {
      const armyBooks = (res.payload as any).results as IArmyBook[];
      const { list, loadedUnits } = this.buildListFromSave(shareData, armyBooks);
      dispatch(loadSavedList({ ...list, isCloud: true }));
      dispatch(loadSavedUnits(loadedUnits));
      dispatch(getGameRules({ gameSystem, reset: true }));
      callback(armyBooks);
    });
  }

  // #endregion

  private static getDataForSave(list: ListState, units: ISelectedUnit[]): ISavedListState {
    const fullUnits = UnitService.getFullUnitList(units, false);

    const data = {
      ...list,
      activationCount: fullUnits.length,
      modelCount: sumBy(units, (x) => x.size),
      units: units.map((u) => ({
        id: u.id,
        armyId: u.armyId,
        customName: u.customName,
        selectionId: u.selectionId,
        selectedUpgrades: u.selectedUpgrades.map((x) => ({
          instanceId: x.instanceId,
          upgradeId: x.upgrade.uid,
          optionId: x.option.uid
        })),
        combined: u.combined,
        joinToUnit: u.joinToUnit,
        xp: u.xp,
        traits: u.traits,
        notes: u.notes
      })),
      modified: new Date().toJSON()
    };
    return data;
  }

  private static updateSaveData(listId: string, modifySaveFunc: (save: ISaveData) => ISaveData) {
    const key = this.getSaveKey(listId);
    const localSave = localStorage[key];
    if (!localSave) return;

    const saveData = modifySaveFunc(JSON.parse(localSave));

    localStorage[key] = JSON.stringify(saveData);

    return saveData;
  }

  public static buildListFromSave(savedList: ISavedListState, armyBooks: IArmyBook[]) {
    const sectionsByBook = armyBooks.reduce(
      (result, book) => ({
        ...result,
        [book.uid]: book.upgradePackages.flatMap((pkg) => pkg.sections)
      }),
      {}
    );
    const unitDefinitions = armyBooks.flatMap((book) => book.units.map((u) => ({ ...u, armyId: book.uid })));
    const { units, ...list } = savedList;
    const loadedUnits = units
      .map((u) => {
        const unitDefinition: IUnit = unitDefinitions.find((unit) => unit.id === u.id);
        // If unit has been removed...
        if (!unitDefinition) return null;
        const unit: ISelectedUnit = {
          ...unitDefinition,
          ...u,
          weapons: makeCopy(unitDefinition.weapons),
          selectedUpgrades: [],
          loadout: makeCopy(unitDefinition.weapons)
        };

        const upgradeSections = sectionsByBook[unit.armyId];

        for (let upg of makeCopy(u.selectedUpgrades)) {
          const section = upgradeSections.find((sec) => sec.uid === upg.upgradeId);
          const option = section?.options.find((opt) => opt.id === upg.optionId);
          if (option) {
            try {
              UpgradeService.apply(unit, section, option);
            } catch {
              console.error("Error loading upgrade", option);
              console.error("For section", section);
              console.error("For unit", unit);
            }
          }
        }
        return UpgradeService.buildUpgrades(unit);
      })
      .filter((x) => Boolean(x));

    return { list, loadedUnits };
  }

  private static getListAsText(
    gameSystemId: GameSystems,
    list: ListState,
    army: ArmyState,
    units: ISelectedUnit[]
  ) {
    const hasListName = army.loadedArmyBooks.length > 1 || list.name !== army.loadedArmyBooks[0].name;
    const points = calculateListTotal(units);
    const unitCount = units.filter((x) => !x.joinToUnit).length;

    const lines = [
      `++ ${hasListName ? list.name + " - " : ""}${army.loadedArmyBooks
        .map((x) => `${x.name} (v${x.versionString})`)
        .join(", ")} [${GameSystems[gameSystemId].toUpperCase()} ${points}pts] [${unitCount} Units] ${
        army.loadedArmyBooks.some((x) => !x.official && !x.balanceValid) ? "**Balance Invalid** " : ""
      }++\n`
    ];

    const constructLabel = (item: IUpgradeGains) => {
      const weapon = item as IUpgradeGainsWeapon;
      const range = weapon.range ? `${weapon.range}", ` : "";
      const attacks = weapon.attacks ? `A${weapon.attacks}` : "";
      const rules = weapon.specialRules?.map((x) => RulesService.displayName(x)).join(", ");

      if (!range && !attacks && !rules) return item.name;

      return `${item.name} (${range}${attacks}${rules?.length > 0 ? ", " + rules : ""})`;
    };
    const getWeapons = (unit: ISelectedUnit) => {
      const allWeapons = unit.loadout
        .concat(unit.loadout.flatMap((x) => (x as any).content ?? []))
        .filter((x) => x.type === GainType.Weapon);
      const loadoutGroups = groupBy(allWeapons, (x) => constructLabel(x));
      const loadoutParts = Object.keys(loadoutGroups).map((key) => {
        const group = loadoutGroups[key];
        const count = group.reduce((sum, next) => sum + next.count, 0);
        return `${count > 1 ? `${count}x ` : ""}${key}`;
      });
      return loadoutParts.join(", ");
    };

    const getRules = (unit: ISelectedUnit) => {
      const baseRules = getUnitRules(unit as any, true);
      const unitUpgradeRules: ISpecialRule[] = UnitService.getAllUpgradedRules(unit);
      const specialRules = baseRules.map((x) => ({ ...x, count: unit.size })).concat(unitUpgradeRules as any);
      const displayRules = RulesService.group(specialRules).map((rule) =>
        RulesService.displayName(rule, rule.count)
      );

      return displayRules.concat(unit.traits).join(", ");
    };

    const fullUnits = UnitService.getFullUnitList(units, true);
    const unitGroups = UnitService.getGroupedDisplayUnits(fullUnits);

    const writeLine = (
      unit: ISelectedUnit,
      count: number,
      endWithNewline: boolean = true,
      pointsCost: number = null
    ) => {
      const name = unit.customName || unit.name;
      const cost = pointsCost ?? calculateUnitTotal(unit);
      lines.push(
        `${count > 1 ? count + "x " : ""}${name} [${unit.size}] Q${unit.quality}+ D${
          unit.defense
        }+ | ${cost}pts${unit.xp ? ` | ${unit.xp}XP` : ""} | ` + getRules(unit)
      );
      lines.push(getWeapons(unit) + (endWithNewline ? "\n" : ""));
    };

    for (const key of Object.keys(unitGroups)) {
      const group = unitGroups[key];

      for (const hero of group.filter((x) => x.hero).map((x) => x.hero)) {
        writeLine(hero, 1, false);
        lines.push("| Joined to:");
      }
      // TODO: Campaign unit pt cost...?
      writeLine(group[0].unit, group.length, true, group[0].unitPoints);
    }

    return lines.join("\n");
  }

  // #endregion

  public static toggleFavourite(save: ISaveData, isFavourite: boolean) {
    const key = this.getSaveKey(save.id);
    localStorage[key] = JSON.stringify({
      ...save,
      favourite: isFavourite
    });
  }

  // #region Sharing

  public static download(list: ListState) {
    const saveData: any = this.getSaveData(list.id);
    const blob = new Blob([saveData], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${list.name}${list.id}.json`;
    document.body.appendChild(a);
    a.dispatchEvent(new MouseEvent("click"));
  }

  public static copyAsText(
    gameSystemId: GameSystems,
    list: ListState,
    army: ArmyState,
    units: ISelectedUnit[]
  ) {
    navigator.clipboard
      .writeText(this.getListAsText(gameSystemId, list, army, units))
      .then(() => console.log("Copied to clipboard..."));
  }

  public static getSaveData(listId: string): string {
    const saveData = localStorage[this.getSaveKey(listId)];
    return saveData;
  }

  // #endregion

  // #region View Preferences

  public static saveViewPreferences(prefs: IViewPreferences) {
    localStorage[STORAGE_KEYS.VIEW_PREFS] = JSON.stringify(prefs);
  }

  public static getViewPreferences() {
    const prefs = localStorage[STORAGE_KEYS.VIEW_PREFS];
    if (!prefs) return null;
    return JSON.parse(prefs) as IViewPreferences;
  }

  // #endregion

  public static getDataForFtlSave(list: ListState, ships: IFleetShipSelection[]): IFtlSavedListState {
    return {
      ...list,
      ships: ships.map((u) => ({
        id: u.id,
        fleetId: u.fleetId,
        selectionId: u.selectionId,
        customName: u.customName,
        notes: u.notes,
        hero: u.hero?.id,
        title: u.title?.id,
        systems: u.systems?.map((x) => ({ id: x.id, damage: x.damage || 0 })),
        weapons: u.weapons?.map((x) => ({
          id: x.id,
          facing: x.facing,
          damage: x.damage || 0
        })),
        count: u.count || 1,
        xp: u.xp,
        traits: u.traits
      }))
    };
  }
}
